import React, { useEffect, useState } from "react";
import {
  Container, Typography, Grid, Paper, Button, Box
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

const AppOperatorDashboard = () => {

  const navigate = useNavigate();
  const { user } = useAuth();

  const [visitHistory, setVisitHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 Role Protection
  useEffect(() => {
    if (!user || user.role !== 'appoperator') {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // 🔹 Fetch Data
  useEffect(() => {
    const fetchVisitHistory = async () => {
      if (!user || !user.tenantId) {
        setVisitHistory([]);
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, "visit_approvals"),
          where("hostUserID", "==", user.tenantId)
        );

        const snapshot = await getDocs(q);
        const visits = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setVisitHistory(visits);

      } catch (error) {
        setVisitHistory([]);
      }

      setLoading(false);
    };

    fetchVisitHistory();
  }, [user]);

  if (!user || user.role !== 'appoperator') return null;

  const actions = [
    {
      title: "Register Visit",
      description: "Schedule and register a new visitor",
      path: "/visitor-registration"
    },
    {
      title: "Checkout Scanner",
      description: "Scan and checkout visitors efficiently",
      path: "/select-visit"
    },
    {
      title: "Visit History",
      description: "View all visits for your assigned organization",
      path: "/app-operator/visits"
    }
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>

      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: "2rem",
            fontWeight: 900,
            fontFamily: "'Unbounded', sans-serif",
            color: "#1c1400"
          }}>
            App Operator Dashboard
          </Typography>

          <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
            Welcome, {user?.userName || user?.email}
          </Typography>

          <Typography sx={{
            color: "#6b7280",
            mt: 1,
            fontSize: "0.95rem"
          }}>
            Manage visitor entries, checkouts, and activity efficiently.
          </Typography>
        </Box>

        {/* ACTION CARDS */}
        <Grid container spacing={3}>
          {actions.map((action, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Paper sx={{
                p: 3,
                borderRadius: "14px",
                border: "1px solid #f0d060",
                background: "#ffffff",
                transition: "0.3s",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)"
                }
              }}>

                <Box>
                  <Typography sx={{
                    fontWeight: 700,
                    color: "#1c1400"
                  }}>
                    {action.title}
                  </Typography>

                  <Typography sx={{
                    color: "#6b7280",
                    fontSize: "0.9rem",
                    mt: 1
                  }}>
                    {action.description}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  sx={{
                    mt: 2,
                    background: "#1c1400",
                    color: "#fde047",
                    borderRadius: "8px",
                    fontWeight: 600,
                    "&:hover": {
                      background: "#f59e0b",
                      color: "#1c1400"
                    }
                  }}
                  onClick={() => navigate(action.path)}
                >
                  Access
                </Button>

              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* INFO BOX */}
        <Box sx={{ mt: 4 }}>
          <Paper sx={{
            p: 3,
            borderRadius: "12px",
            background: "#fffbea",
            border: "1px solid #f0d060"
          }}>
            <Typography sx={{ color: "#6b7280" }}>
              As an app operator, you can register visitors, manage checkouts, and monitor visit activity.
            </Typography>
          </Paper>
        </Box>

      </Container>
    </Box>
  );
};

export default AppOperatorDashboard;